import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fmtDateTime } from "../../lib/time";
import api, { API, formatApiError } from "../../lib/api";
import { useProject } from "../../contexts/ProjectContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Download, RotateCcw, Filter, CalendarDays, List, Gift } from "lucide-react";
import { SelfieThumb } from "../../components/SelfieThumb";
import { fmtBought, unitsBought } from "../../lib/surveyFmt";
import DayWiseStockMatrices from "../../components/charts/DayWiseStockMatrices";

const ALL = "__all";

function buildQS({ frm, to, store, projectId }) {
  const params = new URLSearchParams();
  if (frm) params.set("from", frm);
  if (to) params.set("to", to);
  if (store && store !== ALL) params.set("store_id", store);
  if (projectId && projectId !== ALL) params.set("project_id", projectId);
  const s = params.toString();
  return s ? `?${s}` : "";
}

function downloadCsv(path, filename) {
  const token = localStorage.getItem("ubbs_token");
  fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => {
      if (!r.ok) throw new Error("Failed to export");
      return r.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    })
    .catch((e) => toast.error(e.message));
}

export default function AdminReports() {
  const navigate = useNavigate();
  const { projectId } = useProject();
  const [stores, setStores] = useState([]);
  const [filters, setFilters] = useState({ frm: "", to: "", store: ALL });
  const [shifts, setShifts] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [timesheet, setTimesheet] = useState({ rows: [], totals: { shifts: 0, hours: 0 } });
  const [targetRows, setTargetRows] = useState([]);
  const [giveaways, setGiveaways] = useState([]);
  const [inventory, setInventory] = useState({ needs_project: true, rows: [] });
  const [supplyEdits, setSupplyEdits] = useState({});
  const [savingStore, setSavingStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stockView, setStockView] = useState("daywise"); // 'daywise' | 'entries'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQS({ ...filters, projectId });
      const [a, b, c, t, tr, gv, inv] = await Promise.all([
        api.get(`/admin/shifts${qs}`),
        api.get(`/admin/stocks${qs}`),
        api.get(`/admin/surveys${qs}`),
        api.get(`/admin/timesheet${qs}`),
        api.get(`/admin/target-report${qs}`),
        api.get(`/admin/giveaways${qs}`),
        api.get(`/admin/giveaway-inventory${projectId && projectId !== ALL ? `?project_id=${projectId}` : ""}`),
      ]);
      setShifts(a.data);
      setStocks(b.data);
      setSurveys(c.data);
      setTimesheet(t.data);
      setTargetRows(tr.data || []);
      setGiveaways(gv.data || []);
      setInventory(inv.data || { needs_project: true, rows: [] });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters, projectId]);

  useEffect(() => {
    api
      .get("/admin/stores")
      .then((r) => setStores(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reset = () => setFilters({ frm: "", to: "", store: ALL });

  const saveSupply = async (storeId, override) => {
    if (!projectId || projectId === ALL) return;
    const raw = override !== undefined ? override : supplyEdits[storeId];
    const val = Math.max(0, parseInt(raw, 10) || 0);
    setSavingStore(storeId);
    try {
      await api.put("/admin/giveaway-inventory", {
        project_id: projectId,
        store_id: storeId,
        cases_supplied: val,
      });
      toast.success("Inventory updated");
      setSupplyEdits((e) => {
        const n = { ...e };
        delete n[storeId];
        return n;
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingStore(null);
    }
  };

  const forceCheckout = async (id) => {
    if (!window.confirm("Force check-out this shift now?")) return;
    try {
      await api.post(`/admin/shifts/${id}/force-checkout`);
      toast.success("Shift checked out");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const qs = buildQS({ ...filters, projectId });

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="glass-panel p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4" />
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            Filters
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">From</Label>
            <Input
              type="date"
              value={filters.frm}
              onChange={(e) => setFilters((f) => ({ ...f, frm: e.target.value }))}
              className="mt-2 rounded-xl bg-white"
              data-testid="filter-from"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">To</Label>
            <Input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className="mt-2 rounded-xl bg-white"
              data-testid="filter-to"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Store</Label>
            <Select
              value={filters.store}
              onValueChange={(v) => setFilters((f) => ({ ...f, store: v }))}
            >
              <SelectTrigger className="mt-2 rounded-xl bg-white" data-testid="filter-store">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All stores</SelectItem>
                {stores.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              className="rounded-xl w-full"
              onClick={reset}
              data-testid="filter-reset"
            >
              <RotateCcw className="h-4 w-4 mr-1" /> Reset
            </Button>
          </div>
        </div>
        {loading && (
          <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Loading…
          </div>
        )}
      </div>

      <Tabs defaultValue="attendance" className="w-full">
        <TabsList className="rounded-xl bg-transparent border-b border-slate-200/70 w-full justify-start h-auto p-0 overflow-x-auto">
          <TabsTrigger
            value="attendance"
            className="rounded-xl data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5 py-2 text-xs sm:text-sm"
            data-testid="tab-attendance"
          >
            Attendance ({shifts.length})
          </TabsTrigger>
          <TabsTrigger
            value="stock"
            className="rounded-xl data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5 py-2 text-xs sm:text-sm"
            data-testid="tab-stock"
          >
            Stock ({stocks.length})
          </TabsTrigger>
          <TabsTrigger
            value="surveys"
            className="rounded-xl data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5 py-2 text-xs sm:text-sm"
            data-testid="tab-surveys"
          >
            Surveys ({surveys.length})
          </TabsTrigger>
          <TabsTrigger
            value="timesheet"
            className="rounded-xl data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5 py-2 text-xs sm:text-sm"
            data-testid="tab-timesheet"
          >
            Time Sheet ({timesheet.totals?.shifts || 0})
          </TabsTrigger>
          <TabsTrigger
            value="targets"
            className="rounded-xl data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5 py-2 text-xs sm:text-sm"
            data-testid="tab-targets"
          >
            Targets ({targetRows.length})
          </TabsTrigger>
          <TabsTrigger
            value="giveaways"
            className="rounded-xl data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5 py-2 text-xs sm:text-sm"
            data-testid="tab-giveaways"
          >
            Giveaways ({giveaways.length})
          </TabsTrigger>
          <TabsTrigger
            value="inventory"
            className="rounded-xl data-[state=active]:bg-slate-900 data-[state=active]:text-white px-3 sm:px-5 py-2 text-xs sm:text-sm"
            data-testid="tab-inventory"
          >
            Inventory ({inventory.rows?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="targets" className="mt-6">
          <div className="glass-panel overflow-x-auto" data-testid="targets-table">
            <table className="w-full text-sm">
              <thead className="bg-white/50 border-b border-slate-200/70">
                <tr>
                  {["Date", "Store", "Consumers", "Units sold", "Units target", "Sales status", "Conversion", "Conv. target", "Conv. status"].map((h) => (
                    <th key={h} className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {targetRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-6 text-center text-neutral-500">No data</td>
                  </tr>
                ) : (
                  targetRows.map((r, i) => {
                    const salesHit = r.target_units > 0 ? r.units >= r.target_units : null;
                    const convHit = r.target_conversion > 0 ? r.conversion >= r.target_conversion : null;
                    const badge = (hit) =>
                      hit === null ? (
                        <span className="text-neutral-300">—</span>
                      ) : hit ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border border-emerald-300 text-emerald-700 bg-emerald-50/60">Hit</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border border-red-200 text-red-600 bg-red-50/60">Missed</span>
                      );
                    return (
                      <tr key={`${r.store_id}-${r.date}`} className="border-t border-slate-200/70" data-testid={`target-row-${i}`}>
                        <td className="p-3 font-mono text-xs">{r.date}</td>
                        <td className="p-3 font-medium">{r.store_name}</td>
                        <td className="p-3 font-mono">{r.consumers}</td>
                        <td className="p-3 font-mono font-semibold">{r.units}</td>
                        <td className="p-3 font-mono text-neutral-500">{r.target_units || "—"}</td>
                        <td className="p-3">{badge(salesHit)}</td>
                        <td className="p-3 font-mono font-semibold">{r.conversion}%</td>
                        <td className="p-3 font-mono text-neutral-500">{r.target_conversion ? `${r.target_conversion}%` : "—"}</td>
                        <td className="p-3">{badge(convHit)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="attendance" className="mt-6">
          <div className="flex justify-end mb-3">
            <Button
              className="rounded-xl bg-slate-900 text-white hover:bg-slate-700"
              onClick={() => downloadCsv(`/admin/export/attendance.csv${qs}`, "attendance.csv")}
              data-testid="export-attendance"
            >
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
          </div>
          <div className="glass-panel overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/50 border-b border-slate-200/70">
                <tr>
                  {[
                    { label: "Photos", key: "photos" },
                    { label: "Promoter", key: "promoter" },
                    { label: "Store", key: "store" },
                    { label: "Check-in", key: "checkin" },
                    { label: "Distance", key: "distance-in" },
                    { label: "Check-out", key: "checkout" },
                    { label: "Distance", key: "distance-out" },
                    { label: "Ack", key: "ack" },
                    { label: "Status", key: "status" },
                  ].map((h) => (
                    <th
                      key={h.key}
                      className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500"
                    >
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shifts.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-6 text-center text-neutral-500">
                      No data
                    </td>
                  </tr>
                ) : (
                  shifts.map((s) => (
                    <tr
                      key={s.id}
                      className="border-t border-slate-200/70 cursor-pointer hover:bg-white/50"
                      onClick={() => navigate(`/admin/shifts/${s.id}`)}
                      data-testid={`shift-row-${s.id}`}
                    >
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <SelfieThumb path={s.check_in_photo} size={48} alt="check-in" />
                          <SelfieThumb path={s.check_out_photo} size={48} alt="check-out" />
                        </div>
                      </td>
                      <td className="p-3">{s.promoter?.name || "—"}</td>
                      <td className="p-3">{s.store?.name || "—"}</td>
                      <td className="p-3 font-mono text-xs">
                        {s.check_in_at ? fmtDateTime(s.check_in_at) : "—"}
                      </td>
                      <td className="p-3 font-mono text-xs">
                        {s.check_in_distance_m != null ? `${Math.round(s.check_in_distance_m)}m` : "—"}
                      </td>
                      <td className="p-3 font-mono text-xs">
                        {s.check_out_at ? fmtDateTime(s.check_out_at) : "—"}
                      </td>
                      <td className="p-3 font-mono text-xs">
                        {s.check_out_distance_m != null ? `${Math.round(s.check_out_distance_m)}m` : "—"}
                      </td>
                      <td className="p-3" data-testid={`shift-ack-${s.id}`}>
                        {s.acknowledged_at ? (
                          <span className="text-emerald-600 font-semibold" title={`Acknowledged ${fmtDateTime(s.acknowledged_at)}`}>✓</span>
                        ) : (
                          <span className="text-neutral-300">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest border ${
                              s.status === "active"
                                ? "border-emerald-500 text-emerald-600"
                                : "border-slate-300/70 text-neutral-600"
                            }`}
                          >
                            {s.status}
                          </span>
                          {s.status === "active" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                forceCheckout(s.id);
                              }}
                              className="px-2 py-1 text-[10px] uppercase tracking-widest border border-red-500 text-red-600 hover:bg-red-50"
                              data-testid={`force-checkout-${s.id}`}
                            >
                              Force out
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="stock" className="mt-6">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="inline-flex glass-panel">
              <button
                onClick={() => setStockView("daywise")}
                data-testid="admin-stock-view-daywise"
                className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center gap-2 ${
                  stockView === "daywise" ? "bg-slate-900 text-white" : "bg-white text-neutral-600"
                }`}
              >
                <CalendarDays className="h-3.5 w-3.5" /> Day-wise matrix
              </button>
              <button
                onClick={() => setStockView("entries")}
                data-testid="admin-stock-view-entries"
                className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest flex items-center gap-2 border-l border-slate-200/70 ${
                  stockView === "entries" ? "bg-slate-900 text-white" : "bg-white text-neutral-600"
                }`}
              >
                <List className="h-3.5 w-3.5" /> Entries
              </button>
            </div>
            {stockView === "entries" && (
              <Button
                className="rounded-xl bg-slate-900 text-white hover:bg-slate-700"
                onClick={() => downloadCsv(`/admin/export/stock.csv${qs}`, "stock.csv")}
                data-testid="export-stock"
              >
                <Download className="h-4 w-4 mr-1" /> CSV
              </Button>
            )}
          </div>

          {stockView === "daywise" && (
            <DayWiseStockMatrices
              rows={stocks.map((s) => ({
                created_at: s.created_at,
                store_name: s.store?.name || "—",
                items: (s.sku_details || []).map((sd) => ({
                  sku: sd.sku?.name || "—",
                  qty: sd.qty,
                })),
              }))}
              filenamePrefix="admin-stock-matrix"
              testidPrefix="admin-stock"
            />
          )}

          {stockView === "entries" && (
            <div className="space-y-4">
              {stocks.length === 0 && (
                <div className="glass-panel p-6 text-center text-neutral-500">No data</div>
              )}
              {stocks.map((row) => (
                <div key={row.id} className="glass-panel p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-medium">{row.promoter?.name || "—"}</div>
                      <div className="text-xs text-neutral-500">{row.store?.name || "—"}</div>
                    </div>
                    <div className="font-mono text-xs text-neutral-500">
                      {row.created_at && fmtDateTime(row.created_at)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {row.sku_details.map((sd) => (
                      <div
                        key={sd.sku?.id || sd.sku?.name || JSON.stringify(sd)}
                        className="flex items-center justify-between px-3 py-2 bg-white/50 border border-slate-200/50"
                      >
                        <span className="text-sm">{sd.sku?.name || "?"}</span>
                        <span className="font-mono font-semibold">{sd.qty}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="surveys" className="mt-6">
          <div className="flex justify-end mb-3">
            <Button
              className="rounded-xl bg-slate-900 text-white hover:bg-slate-700"
              onClick={() => downloadCsv(`/admin/export/surveys.csv${qs}`, "surveys.csv")}
              data-testid="export-surveys"
            >
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
          </div>
          <div className="glass-panel overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/50 border-b border-slate-200/70">
                <tr>
                  {["Date", "Promoter", "Store", "Nationality", "Heard", "Current", "Bought", "Units", "Nicotine", "Flavor", "Giveaway"].map((h) => (
                    <th
                      key={h}
                      className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {surveys.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="p-6 text-center text-neutral-500">
                      No data
                    </td>
                  </tr>
                ) : (
                  surveys.map((s) => (
                    <tr key={s.id} className="border-t border-slate-200/70">
                      <td className="p-3 font-mono text-xs">
                        {fmtDateTime(s.created_at)}
                      </td>
                      <td className="p-3">{s.promoter?.name || "—"}</td>
                      <td className="p-3">{s.store?.name || "—"}</td>
                      <td className="p-3">{s.customer_nationality}</td>
                      <td className="p-3">{s.heard_about_ubbs}</td>
                      <td className="p-3">{s.current_product}</td>
                      <td className="p-3 text-xs">
                        {fmtBought(s)}
                      </td>
                      <td className="p-3 font-mono font-semibold" data-testid={`survey-units-${s.id}`}>
                        {unitsBought(s)}
                      </td>
                      <td className="p-3">{s.nicotine_level}</td>
                      <td className="p-3">{s.flavor}</td>
                      <td className="p-3" data-testid={`survey-giveaway-${s.id}`}>
                        {s.giveaway_cases > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                            <Gift className="h-3 w-3" />
                            {s.giveaway_cases}
                            <span className="text-[10px] text-neutral-400">
                              {s.giveaway_reason === "competitor_switcher" ? "switcher" : "std"}
                            </span>
                          </span>
                        ) : (
                          <span className="text-neutral-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="timesheet" className="mt-6">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              Timezone: {timesheet.timezone || "—"} · Total{" "}
              <span className="font-semibold text-black">
                {timesheet.totals?.hours || 0}h
              </span>{" "}
              across {timesheet.totals?.shifts || 0} shifts
            </div>
            <Button
              className="rounded-xl bg-slate-900 text-white hover:bg-slate-700"
              onClick={() => downloadCsv(`/admin/export/timesheet.csv${qs}`, "timesheet.csv")}
              data-testid="export-timesheet"
            >
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
          </div>
          <div className="glass-panel overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/50 border-b border-slate-200/70">
                <tr>
                  {["Date", "Day", "Store", "Promoter", "Shift", "In", "Out", "Hours", "Status"].map((h) => (
                    <th
                      key={h}
                      className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="timesheet-rows">
                {!timesheet.rows || timesheet.rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-6 text-center text-neutral-500">
                      No data
                    </td>
                  </tr>
                ) : (
                  timesheet.rows.map((r) => (
                    <tr
                      key={r.shift_id}
                      className="border-t border-slate-200/70 cursor-pointer hover:bg-white/50"
                      onClick={() => navigate(`/admin/shifts/${r.shift_id}`)}
                      data-testid={`timesheet-row-${r.shift_id}`}
                    >
                      <td className="p-3 font-mono">{r.date}</td>
                      <td className="p-3 font-mono text-xs uppercase">{r.weekday}</td>
                      <td className="p-3">{r.store_name || "—"}</td>
                      <td className="p-3">{r.promoter_name || "—"}</td>
                      <td className="p-3 font-mono text-xs text-neutral-500">
                        {r.scheduled_start && r.scheduled_end
                          ? `${r.scheduled_start} – ${r.scheduled_end}`
                          : "—"}
                      </td>
                      <td
                        className={`p-3 font-mono ${
                          r.late_check_in ? "text-rose-700 font-bold bg-rose-50" : ""
                        }`}
                        title={r.late_check_in ? "Late check-in — calculated from actual time" : ""}
                        data-testid={`timesheet-in-${r.shift_id}`}
                      >
                        {r.check_in_time || "—"}
                        {r.late_check_in && (
                          <span className="ml-1 text-[9px] uppercase tracking-widest">late</span>
                        )}
                      </td>
                      <td
                        className={`p-3 font-mono ${
                          r.early_check_out ? "text-rose-700 font-bold bg-rose-50" : ""
                        }`}
                        title={r.early_check_out ? "Early check-out — calculated up to actual time" : ""}
                        data-testid={`timesheet-out-${r.shift_id}`}
                      >
                        {r.check_out_time || "—"}
                        {r.early_check_out && (
                          <span className="ml-1 text-[9px] uppercase tracking-widest">early</span>
                        )}
                      </td>
                      <td className="p-3 font-mono font-semibold">
                        {r.hours != null ? `${r.hours}h` : "—"}
                      </td>
                      <td className="p-3">
                        <span
                          className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border ${
                            r.status === "active"
                              ? "border-emerald-500 text-emerald-600"
                              : "border-slate-300/70 text-neutral-600"
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="giveaways" className="mt-6">
          <div className="glass-panel overflow-x-auto" data-testid="giveaways-table">
            <table className="w-full text-sm">
              <thead className="bg-white/50 border-b border-slate-200/70">
                <tr>
                  {["Date", "Store", "Promoter", "Current product", "Übbs units", "Reason", "Cases given"].map((h) => (
                    <th key={h} className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {giveaways.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-neutral-500">
                      No silicone cases given in this range.
                    </td>
                  </tr>
                ) : (
                  giveaways.map((g) => (
                    <tr
                      key={g.id}
                      className="border-t border-slate-200/70 cursor-pointer hover:bg-white/50"
                      onClick={() => g.shift_id && navigate(`/admin/shifts/${g.shift_id}`)}
                      data-testid={`giveaway-row-${g.id}`}
                    >
                      <td className="p-3 font-mono text-xs">{fmtDateTime(g.created_at)}</td>
                      <td className="p-3">{g.store_name}</td>
                      <td className="p-3">{g.promoter_name}</td>
                      <td className="p-3">{g.current_product || "—"}</td>
                      <td className="p-3 font-mono font-semibold">{g.ubbs_units}</td>
                      <td className="p-3">
                        <span
                          className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest border rounded-full ${
                            g.giveaway_reason === "competitor_switcher"
                              ? "border-violet-400 text-violet-700"
                              : "border-emerald-400 text-emerald-700"
                          }`}
                        >
                          {g.reason_label}
                        </span>
                      </td>
                      <td className="p-3 font-mono font-bold text-emerald-700">{g.giveaway_cases}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="inventory" className="mt-6">
          {inventory.needs_project ? (
            <div className="glass-panel p-8 text-center text-neutral-500" data-testid="inventory-needs-project">
              Select a specific project from the top bar to manage its silicone case inventory.
              Inventory renews per project.
            </div>
          ) : (
            <div className="glass-panel overflow-x-auto" data-testid="inventory-table">
              <div className="px-4 pt-4 text-xs text-neutral-500">
                Inventory for <span className="font-semibold text-neutral-700">{inventory.project_name}</span> ·
                set the cases supplied per store for this project only.
              </div>
              <table className="w-full text-sm mt-2">
                <thead className="bg-white/50 border-b border-slate-200/70">
                  <tr>
                    {["Store", "Supplied", "Given", "Remaining", "Rule", "Set supplied"].map((h) => (
                      <th key={h} className="text-left p-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(inventory.rows || []).length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-neutral-500">
                        No giveaway-enabled stores in this project.
                      </td>
                    </tr>
                  ) : (
                    inventory.rows.map((r) => {
                      const edited = supplyEdits[r.store_id];
                      const editing = edited !== undefined;
                      return (
                        <tr key={r.store_id} className="border-t border-slate-200/70" data-testid={`inventory-row-${r.store_id}`}>
                          <td className="p-3 font-medium">{r.store_name}</td>
                          <td className="p-3 font-mono">{r.cases_supplied}</td>
                          <td className="p-3 font-mono text-emerald-700 font-semibold">{r.cases_given}</td>
                          <td className={`p-3 font-mono font-bold ${r.cases_remaining <= 0 ? "text-rose-600" : ""}`} data-testid={`inventory-remaining-${r.store_id}`}>
                            {r.cases_remaining}
                          </td>
                          <td className="p-3 text-xs text-neutral-500">
                            {r.purchase_min > 0 ? `Buy ${r.purchase_min} → ${r.cases_per_reward}` : "—"}
                            {r.allow_multiple ? " · multiple" : ""}
                            {r.competitor_enabled ? " · switcher" : ""}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="0"
                                className="rounded-xl w-24 h-9"
                                value={editing ? edited : r.cases_supplied}
                                onChange={(e) =>
                                  setSupplyEdits((s) => ({ ...s, [r.store_id]: e.target.value }))
                                }
                                data-testid={`inventory-supply-input-${r.store_id}`}
                              />
                              <Button
                                size="sm"
                                className="rounded-xl h-9"
                                disabled={!editing || savingStore === r.store_id}
                                onClick={() => saveSupply(r.store_id)}
                                data-testid={`inventory-save-${r.store_id}`}
                              >
                                Save
                              </Button>
                              {r.carry_forward_suggestion > 0 && !r.has_record && (
                                <button
                                  type="button"
                                  className="text-[11px] text-emerald-700 underline whitespace-nowrap"
                                  onClick={() => saveSupply(r.store_id, r.carry_forward_suggestion)}
                                  data-testid={`inventory-carry-${r.store_id}`}
                                >
                                  Carry forward {r.carry_forward_suggestion}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
